<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <loading :status="loading.form"/>
        <form @submit.prevent="saveDocumentation">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto flex items-center justify-between gap-4">
                    <div class="min-w-0 flex-1">
                        <h1 class="stage-page-title">Position anlegen</h1>
                    </div>
                    <div class="flex items-center justify-end flex-shrink-0">
                        <router-link class="btn-stage-icon mr-3" to="/workspace/positions">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M15 18l-6-6 6-6"></path>
                            </svg>
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            Speichern
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <div class="w-full xl:w-1/2 mx-auto space-y-4">
                    <div class="bg-white shadow sm:rounded p-6">
                        <div class="stage-filter-label">Titel</div>
                        <input
                            id="title"
                            v-model="documentation.title"
                            class="stage-filter-input form-input block w-full text-sm transition ease-in-out duration-150"
                            maxlength="255"
                            required
                        >

                        <div class="grid grid-cols-1 gap-4 mt-4">
                            <div>
                                <div class="stage-filter-label">Leistungskonto</div>
                                <input-select
                                    id="position_service_account_id"
                                    v-model="documentation.service_account_id"
                                    class="stage-filter-select"
                                    :options="serviceAccountOptions"
                                    option-label="name"
                                    required
                                >
                                    <template v-slot:selectedOption="props">
                                        <template v-if="!props || !props.anySelected">
                                            <span class="block truncate">Leistungskonto auswählen</span>
                                        </template>
                                        <template v-else>
                                            <div class="flex items-center space-x-3 min-w-0">
                                                <img
                                                    :src="resolveCompanyAvatar(props.option)"
                                                    :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                    class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                >
                                                <div class="min-w-0">
                                                    <span class="block truncate">{{ props.option ? props.option.name : 'Leistungskonto auswählen' }}</span>
                                                    <span v-if="props.option" class="block truncate text-xs text-gray-500">{{ serviceAccountSubtitle(props.option) }}</span>
                                                </div>
                                            </div>
                                        </template>
                                    </template>
                                    <template v-slot:selectOption="props">
                                        <div class="flex items-center space-x-3 min-w-0">
                                            <img
                                                :src="resolveCompanyAvatar(props.option)"
                                                :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                            >
                                            <div
                                                class="block truncate"
                                                :class="props.option && Number(props.option.id) === Number(documentation.service_account_id) ? 'font-semibold' : 'font-normal'"
                                            >
                                                <span class="block">{{ props.option ? props.option.name : '' }}</span>
                                                <span v-if="props.option" class="block text-xs text-gray-500 font-normal">{{ serviceAccountSubtitle(props.option) }}</span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:notFound>
                                        <div class="flex items-center space-x-3">
                                            <div class="font-normal block truncate">
                                                Keine Leistungskonten verfügbar
                                            </div>
                                        </div>
                                    </template>
                                </input-select>
                            </div>

                            <div>
                                <div class="stage-filter-label">Mitarbeitende Dienstleister</div>
                                <div class="border border-bordergray-100 rounded bg-white">
                                    <template v-if="employeeOptions.length > 0">
                                        <label
                                            v-for="employee in employeeOptions"
                                            :key="'new-position-employee-' + employee.id"
                                            class="flex items-center px-3 py-2 border-b border-bordergray-100 last:border-b-0 cursor-pointer"
                                        >
                                            <input
                                                v-model="documentation.assigned_employee_ids"
                                                :value="Number(employee.id)"
                                                class="form-checkbox h-4 w-4 text-gray-700"
                                                type="checkbox"
                                            >
                                            <div class="ml-3 h-5 w-5 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                <img
                                                    :alt="employee.name || 'Mitarbeiter'"
                                                    :src="employee.avatar_url || employee.avatar || employee.gravatar || '/images/default/icon.png'"
                                                    class="h-full w-full object-cover"
                                                >
                                            </div>
                                            <div class="ml-3 min-w-0">
                                                <div class="text-sm text-gray-800 truncate">{{ employee.name }}</div>
                                                <div class="text-xs text-gray-500 truncate">{{ employee.provider_company_name || 'Kein Dienstleister' }}</div>
                                            </div>
                                        </label>
                                    </template>
                                    <div v-else class="px-3 py-3 text-sm text-gray-500">Keine Mitarbeitenden verfügbar</div>
                                </div>
                            </div>
                        </div>

                        <div class="stage-filter-label mt-4">Beschreibung</div>
                        <div class="documentation-editor">
                            <input-wysiwyg
                                id="documentation_body_new"
                                v-model="documentation.body"
                                :plugins="{images: true, cannedReply: false, attachment: true, shortCode: false, menuPoint: false}"
                                @selectUploadFile="selectUploadFile"
                            >
                                <template v-slot:top>
                                    <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                        <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                    </div>
                                </template>
                            </input-wysiwyg>
                        </div>
                        <div v-if="documentation.attachments.length > 0" class="mt-4">
                            <div class="flex flex-wrap items-start gap-2">
                                <template v-for="(attachment, index) in documentation.attachments">
                                    <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white shadow sm:rounded p-6">
                        <div class="stage-filter-label">Prozesskategorie</div>
                        <select
                            id="process_step_category_id"
                            v-model.number="documentation.process_step_category_id"
                            class="stage-filter-input form-select block w-full text-sm transition ease-in-out duration-150 mb-4"
                            required
                            @change="onProcessStepCategoryChange"
                        >
                            <option v-for="category in processStepCategoryList" :key="'new-position-process-category-' + category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>

                        <div class="stage-filter-label">Prozessschritte</div>
                        <template v-if="processStepList.length > 0">
                            <div class="space-y-2">
                                <label
                                    v-for="processStep in processStepList"
                                    :key="'new-position-process-step-' + processStep.id"
                                    class="flex items-start rounded border border-bordergray-100 bg-white px-4 py-3 cursor-pointer"
                                    :style="{ borderLeftWidth: '4px', borderLeftColor: processStep.color || 'var(--ui-border-default)' }"
                                >
                                    <input
                                        v-if="!isMandatoryBoundaryStep(processStep)"
                                        v-model="documentation.process_step_ids"
                                        :value="processStep.id"
                                        class="form-checkbox h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0"
                                        type="checkbox"
                                        @change="onProcessStepsChange"
                                    >
                                    <div
                                        class="min-w-0"
                                        :class="!isMandatoryBoundaryStep(processStep) ? 'ml-3' : ''"
                                    >
                                        <div class="text-sm font-semibold leading-5 text-gray-900">
                                            {{ processStep.name }}
                                        </div>
                                        <div v-if="processStep.short_description" class="text-xs leading-5 text-gray-500 truncate">
                                            {{ processStep.short_description }}
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </template>
                        <div v-else class="text-sm text-gray-500">Keine Prozessschritte verfügbar</div>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </div>
</template>

<script>
export default {
    name: "position-new",
    metaInfo() {
        return {
            title: "Position anlegen"
        }
    },
    data() {
        return {
            loading: {
                form: false,
                file: false,
            },
            uploadingFileProgress: 0,
            processStepList: [],
            processStepCategoryList: [],
            serviceAccountOptions: [],
            employeeOptions: [],
            documentation: {
                title: '',
                body: '',
                requires_customer_cost_approval: false,
                process_step_category_id: null,
                process_step_ids: [],
                current_process_step_id: null,
                service_account_id: null,
                assigned_employee_ids: [],
                attachments: [],
            }
        };
    },
    mounted() {
        this.getFilters();
    },
    methods: {
        getFilters() {
            const self = this;
            self.loading.form = true;
            axios.get('api/workspace/positions/filters').then(function (response) {
                self.processStepCategoryList = response.data.process_step_categories || [];
                self.serviceAccountOptions = Array.isArray(response.data.service_accounts)
                    ? response.data.service_accounts
                    : [];
                self.employeeOptions = Array.isArray(response.data.assignable_employees) ? response.data.assignable_employees : [];
                self.documentation.process_step_category_id = self.processStepCategoryList.length > 0
                    ? self.processStepCategoryList[0].id
                    : null;
                self.documentation.service_account_id = null;
                self.applySelectedProcessStepCategory();
                self.loading.form = false;
            }).catch(function (error) {
                self.loading.form = false;
                if (error && error.response && Number(error.response.status) === 403) {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: 'Positionen können nur von Admins angelegt werden.',
                        type: 'error'
                    });
                    self.$router.replace('/workspace/positions');
                }
            });
        },
        resolveCompanyAvatar(company) {
            if (!company) {
                return '/images/default/icon.png';
            }

            return company.avatar_url || company.avatar || company.image || company.gravatar || '/images/default/icon.png';
        },
        serviceAccountSubtitle(account) {
            if (!account) {
                return '';
            }

            const holder = account.holder_name || (account.holder_type === 'private_customer' ? 'Privatkunde' : 'Unternehmen');
            return holder;
        },
        saveDocumentation() {
            const self = this;
            self.loading.form = true;
            const payload = {
                ...self.documentation,
                requires_customer_cost_approval: false,
                process_step_ids: self.documentation.process_step_ids || [],
                current_process_step_id: self.documentation.current_process_step_id,
                process_step_category_id: self.documentation.process_step_category_id,
                service_account_id: self.documentation.service_account_id ? Number(self.documentation.service_account_id) : null,
                assigned_employee_ids: (self.documentation.assigned_employee_ids || []).map((id) => Number(id)),
                process_steps: (self.documentation.process_step_ids || []).map((stepId) => ({
                    id: stepId,
                    start_date: null,
                    end_date: null,
                    body: null,
                    is_completed: false,
                })),
            };

            axios.post('api/workspace/positions', payload).then(function (response) {
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/workspace/positions/' + response.data.position.id + '/manage');
            }).catch(function () {
                self.loading.form = false;
            });
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/workspace/positions/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.documentation.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.documentation.attachments.splice(attachment, 1);
        },
        isMandatoryBoundaryStep(processStep) {
            if (!processStep) {
                return false;
            }

            const key = (processStep.system_key || '').toString();
            return key.endsWith('_process_start') || key.endsWith('_process_completion');
        },
        enforceMandatoryProcessSteps() {
            const selectedIds = new Set((this.documentation.process_step_ids || []).map((id) => Number(id)));
            (this.processStepList || []).forEach((processStep) => {
                if (this.isMandatoryBoundaryStep(processStep)) {
                    selectedIds.add(Number(processStep.id));
                }
            });
            this.documentation.process_step_ids = Array.from(selectedIds);
        },
        onProcessStepsChange() {
            this.enforceMandatoryProcessSteps();
            if ((this.documentation.process_step_ids || []).length < 1) {
                this.documentation.current_process_step_id = null;
                return;
            }

            if (!this.documentation.process_step_ids.includes(this.documentation.current_process_step_id)) {
                this.documentation.current_process_step_id = this.documentation.process_step_ids[0];
            }
        },
        onProcessStepCategoryChange() {
            this.applySelectedProcessStepCategory();
        },
        applySelectedProcessStepCategory() {
            const selectedCategoryId = Number(this.documentation.process_step_category_id);
            const selectedCategory = (this.processStepCategoryList || []).find(
                (category) => Number(category.id) === selectedCategoryId
            );
            this.processStepList = selectedCategory && Array.isArray(selectedCategory.process_steps)
                ? selectedCategory.process_steps
                : [];
            this.documentation.process_step_ids = [];
            this.enforceMandatoryProcessSteps();
            this.documentation.current_process_step_id = this.documentation.process_step_ids.length > 0
                ? this.documentation.process_step_ids[0]
                : null;
        },
    }
}
</script>

<style lang="scss">
.documentation-editor .tox.tox-tinymce {
    min-height: 360px;
}
</style>
